(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root?.document) api.mount(root);
})(typeof window === "undefined" ? null : window, function () {
  "use strict";

  const LIMITS = Object.freeze({
    designs: 1000,
    pageSize: 250,
    pages: 4,
    concurrency: 2,
    timeout: 10000,
    bytes: 2 * 1024 * 1024,
  });
  const SERIES = Object.freeze({
    "pocket-pup": 10,
    holiday: 20,
    halloween: 30,
  });
  const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const KEY =
    /^[a-z0-9]+(?:-[a-z0-9]+)*--[a-z0-9]+(?:-[a-z0-9]+)*--[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const HASH = "#catalog:";
  function check(condition, message) {
    if (!condition) throw new Error(message);
  }
  function label(value, max = 500) {
    return (
      typeof value === "string" &&
      value.trim().length > 0 &&
      value.length <= max &&
      !/[\u0000-\u001f\u007f]/u.test(value)
    );
  }
  function integer(value, min, max) {
    const number = Number(value);
    check(
      String(value).trim() !== "" &&
        Number.isInteger(number) &&
        number >= min &&
        number <= max,
      "Invalid integer",
    );
    return number;
  }

  function safeURL(value, base, image = false) {
    check(
      typeof value === "string" &&
        value.length > 0 &&
        value.length <= 2048 &&
        !/[\u0000-\u0020\\]/u.test(value),
      "Invalid URL",
    );
    const origin = new URL(base);
    const url = new URL(value, origin);
    check(
      !url.username &&
        !url.password &&
        ["http:", "https:"].includes(url.protocol),
      "Unsafe URL",
    );
    if (image) {
      check(
        (url.origin === origin.origin && url.pathname.startsWith("/cdn/")) ||
          (url.protocol === "https:" &&
            url.hostname === "cdn.shopify.com" &&
            url.pathname.startsWith("/s/files/")),
        "Unsafe image URL",
      );
    } else {
      check(
        url.origin === origin.origin &&
          /\/(?:products)\/[a-z0-9]+(?:-[a-z0-9]+)*$/.test(url.pathname) &&
          !url.hash &&
          !url.search,
        "Unsafe product URL",
      );
    }
    return url.href;
  }

  // Compare selected sources, not responsive widths or CDN host aliases.
  function imageIdentity(value, base) {
    if (!value) return "";
    const url = new URL(safeURL(value, base, true));
    check(url.searchParams.getAll("v").length <= 1, "Ambiguous image revision");
    return JSON.stringify([
      url.pathname.split("/").at(-1),
      url.searchParams.get("v"),
    ]);
  }

  function imagePresentation(record, small = false) {
    const cropped = record.imageCrop === "pocket-v1";
    const candidates = cropped
      ? [240, 360, 480, 720, 960, 1440, 1920, 2048]
      : [240, 360, 480, 720, 960];
    return {
      widths: [
        ...new Set(candidates.map((width) => Math.min(width, record.width))),
      ],
      sizes: small
        ? cropped
          ? "184px"
          : "80px"
        : cropped
          ? "(min-width: 990px) 57.5vw, 115vw"
          : "(min-width: 990px) 25vw, 50vw",
    };
  }

  function validateRecord(raw, base) {
    check(
      raw && KEY.test(raw.key) && SLUG.test(raw.handle) && SLUG.test(raw.breed),
      "Invalid identity",
    );
    check(
      raw.key.split("--")[0] === raw.breed &&
        raw.key.split("--")[1] === raw.series &&
        Object.hasOwn(SERIES, raw.series),
      "Mismatched identity",
    );
    check(
      label(raw.title, 200) &&
        label(raw.breedLabel, 200) &&
        label(raw.seriesLabel, 200) &&
        label(raw.priceLabel, 200) &&
        label(raw.alt),
      "Invalid labels",
    );
    check(
      typeof raw.variation === "string" &&
        typeof raw.variationLabel === "string" &&
        ((raw.variation === "" && raw.variationLabel === "") ||
          (SLUG.test(raw.variation) && label(raw.variationLabel, 200))),
      "Invalid variation",
    );
    check(
      Number(raw.seriesOrder) === SERIES[raw.series],
      "Invalid Series order",
    );
    const record = {
      ...raw,
      url: safeURL(raw.url, base),
      seriesOrder: SERIES[raw.series],
      image: "",
      width: 0,
      height: 0,
      imageCrop: raw.imageCrop === undefined ? "none" : raw.imageCrop,
    };
    check(
      ["none", "pocket-v1"].includes(record.imageCrop),
      "Invalid image crop",
    );
    if (raw.image) {
      record.image = safeURL(raw.image, base, true);
      record.width = integer(raw.width, 1, 100000);
      record.height = integer(raw.height, 1, 100000);
      imageIdentity(record.image, base);
    }
    check(record.image || record.imageCrop === "none", "Crop without image");
    check(
      typeof raw.eventPayload === "string" && raw.eventPayload.length <= 16384,
      "Invalid event payload",
    );
    const event = JSON.parse(raw.eventPayload);
    check(
      event && typeof event === "object" && !Array.isArray(event),
      "Invalid event payload",
    );
    return Object.freeze(record);
  }

  function filterSignature(query) {
    const pairs = [...new URLSearchParams(query)].filter(([key]) =>
      key.startsWith("filter."),
    );
    pairs.sort(([ak, av], [bk, bv]) =>
      ak < bk ? -1 : ak > bk ? 1 : av < bv ? -1 : av > bv ? 1 : 0,
    );
    return new URLSearchParams(pairs).toString();
  }

  function parsePage(html) {
    const document = new DOMParser().parseFromString(html, "text/html");
    const feeds = document.querySelectorAll("[data-catalog-feed]");
    check(
      feeds.length === 1 && !document.querySelector("[data-catalog-invalid]"),
      "Invalid feed",
    );
    return {
      ...feeds[0].dataset,
      records: [...feeds[0].querySelectorAll("[data-catalog-record]")].map(
        (element) => ({ ...element.dataset }),
      ),
    };
  }

  function validatePage(page, number, context, base) {
    check(page && Number(page.schema) === 2, "Unsupported feed schema");
    check(integer(page.page, 1, LIMITS.pages) === number, "Wrong page");
    check(
      integer(page.total, 0, LIMITS.designs) === context.total,
      "Changed total",
    );
    const pages = Math.max(1, Math.ceil(context.total / LIMITS.pageSize));
    check(
      integer(page.pages, 1, LIMITS.pages) === pages,
      "Wrong page coverage",
    );
    for (const key of ["release", "fingerprint", "locale", "country"])
      check(page[key] === context[key], `Changed ${key}`);
    check(
      new URL(page.url, base).href === new URL(context.url, base).href &&
        filterSignature(page.filters) === filterSignature(context.filters),
      "Changed collection/filter context",
    );
    const count = Math.min(
      LIMITS.pageSize,
      context.total - (number - 1) * LIMITS.pageSize,
    );
    check(
      Array.isArray(page.records) && page.records.length === count,
      "Incomplete page",
    );
    return page.records.map((record) => validateRecord(record, base));
  }

  async function loadAll({
    url,
    context,
    signal,
    fetchImpl = fetch,
    parse = parsePage,
    timeout = LIMITS.timeout,
  }) {
    integer(context.total, 0, LIMITS.designs);
    check(
      label(context.release, 80) && /^[a-f0-9]{64}$/.test(context.fingerprint),
      "Invalid release",
    );
    if (context.total === 0) return [];
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) abort();
    let bytes = 0;
    async function request(number) {
      const requestURL = new URL(url);
      requestURL.hash = "";
      requestURL.searchParams.delete("sections");
      requestURL.searchParams.delete("view");
      requestURL.searchParams.set("section_id", "catalog-design-data");
      requestURL.searchParams.set("page", String(number));
      let timer;
      const timedOut = new Promise((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error("Feed timeout"));
        }, timeout);
      });
      const cancelled = new Promise((_, reject) => {
        if (controller.signal.aborted) reject(new Error("Feed aborted"));
        else
          controller.signal.addEventListener(
            "abort",
            () => reject(new Error("Feed aborted")),
            { once: true },
          );
      });
      try {
        return await Promise.race([
          (async () => {
            const response = await fetchImpl(requestURL.href, {
              signal: controller.signal,
              credentials: "same-origin",
              cache: "no-store",
              headers: { Accept: "text/html" },
            });
            check(response.ok && !response.redirected, "Feed request failed");
            const reader = response.body?.getReader();
            check(reader, "Streaming response required");
            const decoder = new TextDecoder("utf-8", { fatal: true });
            let html = "";
            try {
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                bytes += value.byteLength;
                check(
                  bytes <= LIMITS.bytes && !controller.signal.aborted,
                  "Feed budget exceeded or aborted",
                );
                html += decoder.decode(value, { stream: true });
              }
              html += decoder.decode();
            } finally {
              await reader.cancel().catch(() => {});
            }
            check(!controller.signal.aborted, "Feed aborted");
            return validatePage(parse(html), number, context, url);
          })(),
          timedOut,
          cancelled,
        ]);
      } finally {
        clearTimeout(timer);
      }
    }
    try {
      const pages = Math.ceil(context.total / LIMITS.pageSize);
      const results = [await request(1)];
      let next = 2;
      await Promise.all(
        Array.from(
          { length: Math.min(LIMITS.concurrency, pages - 1) },
          async () => {
            while (next <= pages) {
              const number = next++;
              results[number - 1] = await request(number);
            }
          },
        ),
      );
      const records = results.flat();
      check(
        records.length === context.total &&
          new Set(records.map((record) => record.key)).size ===
            records.length &&
          new Set(records.map((record) => record.handle)).size ===
            records.length,
        "Duplicate or missing Design",
      );
      return records;
    } finally {
      controller.abort();
      signal?.removeEventListener("abort", abort);
    }
  }

  function groupSeries(records) {
    const groups = new Map();
    for (const record of records) {
      if (!groups.has(record.series))
        groups.set(record.series, {
          slug: record.series,
          label: record.seriesLabel,
          order: record.seriesOrder,
          records: [],
        });
      const group = groups.get(record.series);
      check(
        group.label === record.seriesLabel &&
          group.order === record.seriesOrder,
        "Inconsistent Series metadata",
      );
      group.records.push(record);
    }
    return [...groups.values()]
      .sort((a, b) => a.order - b.order)
      .map((group) => ({
        ...group,
        thumbnail: group.records.find((record) => record.image) || null,
      }));
  }

  function readState(hash) {
    if (!hash.startsWith(HASH) || hash.length > 1024)
      return { view: "designs", series: "", page: 1 };
    const params = new URLSearchParams(hash.slice(HASH.length));
    const series = params.get("series") || "";
    const page = Number(params.get("designPage") || 1);
    return {
      view: params.get("view") === "series" ? "series" : "designs",
      series: Object.hasOwn(SERIES, series) ? series : "",
      page: Number.isInteger(page) && page >= 1 && page <= 63 ? page : 1,
    };
  }
  function writeState(state) {
    const params = new URLSearchParams({
      view: state.view === "series" ? "series" : "designs",
    });
    if (state.view === "series" && Object.hasOwn(SERIES, state.series)) {
      params.set("series", state.series);
      params.set("designPage", String(state.page));
    }
    return HASH + params.toString();
  }
  function canonicalStateHash(hash) {
    if (!hash.startsWith(HASH) || hash.length > 1024) return "";
    const canonical = writeState(readState(hash));
    return canonical === hash ? canonical : "";
  }
  function seriesBrowsingEnabled(document) {
    return Boolean(document.querySelector("[data-catalog-series-browsing]"));
  }
  function clearDisabledCatalogState(window) {
    if (
      seriesBrowsingEnabled(window.document) ||
      !canonicalStateHash(window.location.hash)
    )
      return false;
    const url = new URL(window.location.href);
    url.hash = "";
    window.history.replaceState(window.history.state, "", url.href);
    return true;
  }
  function navigationURL(href, base, state) {
    const url = new URL(href, base);
    check(
      url.origin === new URL(base).origin &&
        ["http:", "https:"].includes(url.protocol),
      "Unsafe navigation",
    );
    for (const key of ["page", "section_id", "sections"])
      url.searchParams.delete(key);
    if (state.view === "series")
      url.hash = writeState({ view: "series", series: "", page: 1 });
    else if (url.hash.startsWith(HASH)) url.hash = "";
    return url;
  }
  function mount(window) {
    window.CatalogDesignBrowser = {
      LIMITS,
      safeURL,
      validateRecord,
      validatePage,
      loadAll,
      groupSeries,
      readState,
      writeState,
      canonicalStateHash,
      seriesBrowsingEnabled,
      clearDisabledCatalogState,
      navigationURL,
    };
    const document = window.document;
    const clearDisabledState = () => clearDisabledCatalogState(window);
    window.addEventListener("popstate", clearDisabledState);
    window.addEventListener("hashchange", clearDisabledState);
    clearDisabledState();

    let serial = 0;
    class CatalogDesignBrowser extends window.HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.generation = 0;
        this.instance = ++serial;
        this.designs = this.querySelector("[data-catalog-designs]");
        this.seriesList = this.querySelector("[data-catalog-series]");
        this.status = this.querySelector("[data-catalog-status]");
        this.count = this.querySelector("[data-catalog-count]");
        this.releaseRoot =
          this.closest("[data-catalog-design-release]") || document;
        this.seriesEnabled = seriesBrowsingEnabled(document);
        this.viewSelects = this.seriesEnabled
          ? [...this.releaseRoot.querySelectorAll("[data-catalog-view-select]")]
          : [];
        this.viewToggles = this.seriesEnabled
          ? [...this.releaseRoot.querySelectorAll("[data-catalog-toggle]")]
          : [];
        this.originalCount = this.count.textContent;
        this.retry = this.querySelector("[data-catalog-retry]");
        this.initialCards = [
          ...this.designs.querySelectorAll("[data-design-key]"),
        ];
        this.cardCache = new Map(
          this.initialCards.map((card) => [card.dataset.designKey, card]),
        );
        this.context = { ...this.dataset, total: Number(this.dataset.total) };
        this.search = window.location.search;
        this.onClick = this.click.bind(this);
        this.onViewChange = (event) => {
          this.transition({
            view: event.target.value,
            series: "",
            page: 1,
          });
        };
        this.onHistory = () => {
          if (window.location.search !== this.search) {
            window.location.reload();
            return;
          }
          if (!this.seriesEnabled) {
            clearDisabledCatalogState(window);
            this.apply({ view: "designs", series: "", page: 1 });
            return;
          }
          this.apply(readState(window.location.hash));
        };
        this.onPageHide = () => {
          this.cancel();
          this.records = null;
          this.restoreDesigns();
        };
        this.onPageShow = (event) => {
          if (event.persisted) this.onHistory();
        };
        this.addEventListener("click", this.onClick);
        for (const select of this.viewSelects)
          select.addEventListener("change", this.onViewChange);
        window.addEventListener("popstate", this.onHistory);
        window.addEventListener("hashchange", this.onHistory);
        window.addEventListener("pagehide", this.onPageHide);
        window.addEventListener("pageshow", this.onPageShow);
        for (const toggle of this.viewToggles)
          toggle.hidden = this.context.total === 0;
        this.onHistory();
      }
      disconnectedCallback() {
        this.cancel();
        this.ready = false;
        this.removeEventListener("click", this.onClick);
        for (const select of this.viewSelects)
          select.removeEventListener("change", this.onViewChange);
        window.removeEventListener("popstate", this.onHistory);
        window.removeEventListener("hashchange", this.onHistory);
        window.removeEventListener("pagehide", this.onPageHide);
        window.removeEventListener("pageshow", this.onPageShow);
      }
      cancel() {
        this.generation += 1;
        this.controller?.abort();
      }
      syncSearch() {
        this.search = window.location.search;
      }
      focusViewSelect() {
        const visible = this.viewSelects.find(
          (select) => select.getClientRects().length > 0,
        );
        (visible || this.viewSelects[0])?.focus();
      }
      restoreDesigns() {
        const restoreFocus = this.seriesList.contains(document.activeElement);
        this.seriesList.hidden = true;
        const list = this.designs.querySelector("ul");
        if (list) list.replaceChildren(...this.initialCards);
        this.designs.hidden = false;
        this.count.textContent = this.originalCount;
        this.setView("designs");
        if (restoreFocus) this.focusViewSelect();
      }
      setView(view) {
        for (const select of this.viewSelects) select.value = view;
      }
      transition(state) {
        if (!this.seriesEnabled) {
          clearDisabledCatalogState(window);
          this.apply({ view: "designs", series: "", page: 1 });
          return;
        }
        const hash = writeState(state);
        if (window.location.hash !== hash)
          window.history.pushState({ catalog: true }, "", hash);
        this.apply(state);
      }
      click(event) {
        const button = event.target.closest("button");
        if (this.seriesEnabled && button?.matches("[data-catalog-retry]"))
          this.apply({ ...readState(window.location.hash), view: "series" });
        else if (
          this.seriesEnabled &&
          button?.matches("[data-catalog-series-toggle]")
        ) {
          const slug = button.dataset.catalogSeriesToggle;
          this.transition({
            view: "series",
            series: this.state?.series === slug ? "" : slug,
            page: 1,
          });
          this.querySelector(`[data-catalog-series-toggle="${slug}"]`)?.focus();
        } else if (
          this.seriesEnabled &&
          button?.matches("[data-catalog-child-page]")
        ) {
          this.transition({
            ...this.state,
            page: Number(button.dataset.catalogChildPage),
          });
          this.querySelector(
            `[data-catalog-panel="${this.state.series}"]`,
          )?.focus();
        }
      }
      async apply(state) {
        this.cancel();
        const generation = this.generation;
        const retryFocused = document.activeElement === this.retry;
        this.state = state;
        this.retry.hidden = true;
        this.status.textContent = "";
        if (
          !this.seriesEnabled ||
          state.view !== "series" ||
          this.context.total === 0
        ) {
          this.restoreDesigns();
          return;
        }
        try {
          if (!this.records) {
            this.controller = new AbortController();
            this.status.textContent = this.dataset.loading;
            const records = await loadAll({
              url: window.location.href,
              context: this.context,
              signal: this.controller.signal,
            });
            if (!this.isConnected || generation !== this.generation) return;
            for (const card of this.cardCache.values()) {
              const record = records.find(
                (entry) => entry.key === card.dataset.designKey,
              );
              this.checkCard(card, record);
            }
            this.records = records;
          }
          if (generation !== this.generation) return;
          this.renderGroups(groupSeries(this.records), state);
          this.status.textContent = "";
          if (retryFocused) this.focusViewSelect();
        } catch (error) {
          if (!this.isConnected || generation !== this.generation) return;
          this.restoreDesigns();
          this.status.textContent = this.dataset.error;
          this.retry.hidden = false;
          if (retryFocused) this.retry.focus();
        }
      }
      node(tag, className, text) {
        const element = document.createElement(tag);
        if (className) element.className = className;
        if (text !== undefined) element.textContent = text;
        return element;
      }
      checkCard(card, record) {
        check(
          record && card.querySelector("a").href === record.url,
          "Cached/feed destination drift",
        );
        check(
          (card.dataset.imageCrop ?? "none") === record.imageCrop &&
            imageIdentity(
              card.querySelector("img")?.getAttribute("src"),
              window.location.href,
            ) === imageIdentity(record.image, window.location.href),
          "Cached/feed image drift",
        );
        const prices = card.querySelectorAll(".catalog-design-card__price");
        check(
          prices.length === 1 && prices[0].textContent === record.priceLabel,
          "Cached/feed price drift",
        );
        const breeds = card.querySelectorAll(".catalog-design-card__breed");
        const variations = card.querySelectorAll(
          ".catalog-design-card__variation",
        );
        const titles = card.querySelectorAll("h3");
        const breedLabel = record.variationLabel
          ? `${record.breedLabel} (${record.variationLabel})`
          : record.breedLabel;
        check(
          breeds.length === 1 &&
            breeds[0].textContent === breedLabel &&
            variations.length === 0 &&
            titles.length === 1 &&
            titles[0].textContent === record.title,
          "Cached/feed card text drift",
        );
      }
      art(record, small = false) {
        if (!record?.image)
          return this.node(
            "span",
            "catalog-design-card__placeholder",
            this.dataset.artworkPending,
          );
        const img = this.node("img");
        img.src = record.image;
        img.alt = small ? "" : record.alt;
        img.width = record.width;
        img.height = record.height;
        img.loading = "lazy";
        const { widths, sizes } = imagePresentation(record, small);
        img.srcset = widths
          .map((width) => {
            const url = new URL(record.image);
            url.searchParams.set("width", String(width));
            return `${url.href} ${width}w`;
          })
          .join(", ");
        img.sizes = sizes;
        if (small) {
          const frame = this.node("span", "catalog-series-cover");
          frame.dataset.imageCrop = record.imageCrop;
          frame.append(img);
          return frame;
        }
        return img;
      }
      card(record) {
        if (this.cardCache.has(record.key)) {
          const cached = this.cardCache.get(record.key);
          this.checkCard(cached, record);
          return cached;
        }
        const card = this.node("li", "catalog-design-card");
        card.dataset.designKey = record.key;
        card.dataset.imageCrop = record.imageCrop;
        const product = this.node("product-component");
        product.setAttribute("view-event-payload", record.eventPayload);
        const link = this.node("a", "catalog-design-card__link");
        link.href = record.url;
        const art = this.node("div", "catalog-design-card__art");
        art.append(this.art(record));
        const breedLabel = record.variationLabel
          ? `${record.breedLabel} (${record.variationLabel})`
          : record.breedLabel;
        link.append(
          art,
          this.node("span", "catalog-design-card__breed", breedLabel),
          this.node("h3", "h3", record.title),
          this.node("span", "catalog-design-card__price", record.priceLabel),
        );
        product.append(link);
        card.append(product);
        this.cardCache.set(record.key, card);
        return card;
      }
      renderGroups(groups, state) {
        // Move/reuse cards instead of cloning instrumented product elements.
        const focusedToggle =
          document.activeElement?.dataset.catalogSeriesToggle;
        const focusedPanel = document.activeElement?.closest(
          "[data-catalog-panel]",
        )?.dataset.catalogPanel;
        this.designs.hidden = true;
        this.seriesList.replaceChildren();
        this.seriesList.hidden = false;
        this.setView("series");
        this.count.textContent = this.dataset.counts
          .replace("[series]", String(groups.length))
          .replace("[designs]", String(this.records.length));
        for (const group of groups) {
          const item = this.node("li", "catalog-series-item");
          const button = this.node("button", "catalog-series-toggle");
          button.type = "button";
          button.dataset.catalogSeriesToggle = group.slug;
          const id = `CatalogSeries-${this.instance}-${group.slug}`;
          button.id = `${id}-button`;
          button.setAttribute("aria-controls", id);
          button.setAttribute(
            "aria-expanded",
            String(state.series === group.slug),
          );
          button.append(
            this.art(group.thumbnail, true),
            this.node(
              "span",
              "",
              `${group.label} · ${this.dataset.childCount.replace("[count]", String(group.records.length))}`,
            ),
          );
          const panel = this.node("div", "catalog-series-panel");
          panel.id = id;
          panel.dataset.catalogPanel = group.slug;
          panel.tabIndex = -1;
          panel.setAttribute("role", "region");
          panel.setAttribute("aria-labelledby", button.id);
          panel.hidden = state.series !== group.slug;
          if (!panel.hidden) {
            const pages = Math.ceil(group.records.length / 16);
            const page = Math.min(state.page, pages);
            this.state = { ...state, page };
            const list = this.node("ul", "catalog-design-grid");
            list.setAttribute("role", "list");
            for (const record of group.records.slice(
              (page - 1) * 16,
              page * 16,
            ))
              list.append(this.card(record));
            panel.append(list);
            if (pages > 1) {
              const nav = this.node("nav", "catalog-series-pagination");
              nav.setAttribute("aria-label", group.label);
              for (const [target, text] of [
                [page - 1, this.dataset.previous],
                [page + 1, this.dataset.next],
              ]) {
                const control = this.node(
                  "button",
                  "button button--secondary",
                  text,
                );
                control.type = "button";
                control.dataset.catalogChildPage = String(target);
                control.disabled = target < 1 || target > pages;
                nav.append(control);
              }
              nav.insertBefore(
                this.node(
                  "span",
                  "",
                  this.dataset.pageLabel
                    .replace("[page]", String(page))
                    .replace("[pages]", String(pages)),
                ),
                nav.lastChild,
              );
              panel.append(nav);
            }
          }
          item.append(button, panel);
          this.seriesList.append(item);
        }
        if (focusedToggle)
          this.querySelector(
            `[data-catalog-series-toggle="${focusedToggle}"]`,
          )?.focus();
        else if (focusedPanel) {
          const target =
            state.series === focusedPanel
              ? "data-catalog-panel"
              : "data-catalog-series-toggle";
          this.querySelector(`[${target}="${focusedPanel}"]`)?.focus();
        }
      }
    }
    if (!window.customElements.get("catalog-design-browser"))
      window.customElements.define(
        "catalog-design-browser",
        CatalogDesignBrowser,
      );
  }

  return {
    LIMITS,
    SERIES,
    safeURL,
    imageIdentity,
    imagePresentation,
    validateRecord,
    filterSignature,
    validatePage,
    loadAll,
    groupSeries,
    readState,
    writeState,
    canonicalStateHash,
    seriesBrowsingEnabled,
    clearDisabledCatalogState,
    navigationURL,
    mount,
  };
});
